import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Eye, EyeOff, Save, Trash2, FileText, Plus,
  ChevronUp, ChevronDown, ArrowLeft,
} from "lucide-react";
import {
  deleteSiteRuleSection,
  getSiteRules,
  listSiteRuleSections,
  upsertSiteRuleSection,
  upsertSiteRules,
} from "@/lib/api";
import { supabase } from "@/lib/supabaseClient";
import SectionRenderer from "@/components/SectionRenderer";
import SectionFormEditor from "@/components/SectionFormEditor";

const SECTION_TYPES = [
  { type: "info_block",     title: "Info block",         defaults: { rows: [] } },
  { type: "ppe_checklist",  title: "PPE checklist",      defaults: { items: [], other: "" } },
  { type: "travel_times",   title: "Travel times",       defaults: { rows: [] } },
  { type: "approach",       title: "Approach to site",   defaults: { routes: [] } },
  { type: "park_up_steps",  title: "Park-up steps",      defaults: { steps: [] } },
  { type: "markdown",       title: "Free text",          defaults: {} },
  { type: "accordion",      title: "Accordion (FAQ)",    defaults: { items: [] } },
  { type: "site_map",       title: "Site map image",     defaults: { image_url: "", caption: "" } },
  { type: "file_upload",    title: "File downloads",     defaults: { files: [] } },
];

export default function SiteRulesEdit() {
  const { id: siteId } = useParams();
  const [site, setSite] = useState(null);
  const [rules, setRules] = useState(null);
  const [sections, setSections] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draftData, setDraftData] = useState({});
  const [draftBody, setDraftBody] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBanner, setDraftBanner] = useState("");
  const [draftIntro, setDraftIntro] = useState("");
  const [headerDirty, setHeaderDirty] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: s }, { data: r }, sec] = await Promise.all([
        supabase.from("lbs_sites").select("id,name,code").eq("id", siteId).maybeSingle(),
        getSiteRules(siteId),
        listSiteRuleSections(siteId),
      ]);
      if (cancelled) return;
      setSite(s);
      setRules(r);
      setDraftBanner(r?.banner_image || "");
      setDraftIntro(r?.intro_markdown || "");
      setHeaderDirty(false);
      if (sec.error) toast.error(sec.error.message);
      setSections(sec.data || []);
    })();
    return () => { cancelled = true; };
  }, [siteId, tick]);

  const startEdit = (sec) => {
    setEditingId(sec.id);
    setDraftData(sec.data || {});
    setDraftBody(sec.body_markdown || "");
    setDraftTitle(sec.title || "");
  };

  const saveEdit = async (sec) => {
    const { error } = await upsertSiteRuleSection({
      id: sec.id,
      site_id: siteId,
      section_type: sec.section_type,
      title: draftTitle || sec.title,
      display_order: sec.display_order,
      body_markdown: draftBody,
      data: draftData,
      is_internal: !!sec.is_internal,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Saved.");
    setEditingId(null);
    refresh();
  };

  /** Toggle is_internal on a section without entering edit mode. */
  const toggleInternal = async (sec) => {
    const { error } = await upsertSiteRuleSection({
      id: sec.id,
      site_id: siteId,
      section_type: sec.section_type,
      title: sec.title,
      display_order: sec.display_order,
      body_markdown: sec.body_markdown || "",
      data: sec.data || {},
      is_internal: !sec.is_internal,
    });
    if (error) { toast.error(error.message); return; }
    toast.success(sec.is_internal ? "Visible to drivers." : "Hidden from public page.");
    refresh();
  };

  const doDelete = async (sec) => {
    if (!window.confirm(`Delete section "${sec.title}"?`)) return;
    const { error } = await deleteSiteRuleSection(sec.id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted."); refresh(); }
  };

  const addSection = async (preset) => {
    const maxOrder = sections.reduce((m, s) => Math.max(m, s.display_order || 0), 0);
    const { error } = await upsertSiteRuleSection({
      site_id: siteId,
      section_type: preset.type,
      title: preset.title,
      display_order: maxOrder + 10,
      data: preset.defaults,
    });
    if (error) { toast.error(error.message); return; }
    toast.success(`Added "${preset.title}".`);
    refresh();
  };

  const move = async (idx, dir) => {
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sections.length) return;
    const a = sections[idx];
    const b = sections[swapIdx];
    // Swap display_order
    const { error: e1 } = await upsertSiteRuleSection({
      id: a.id, site_id: a.site_id, section_type: a.section_type, title: a.title,
      display_order: b.display_order, body_markdown: a.body_markdown, data: a.data,
    });
    const { error: e2 } = await upsertSiteRuleSection({
      id: b.id, site_id: b.site_id, section_type: b.section_type, title: b.title,
      display_order: a.display_order, body_markdown: b.body_markdown, data: b.data,
    });
    if (e1 || e2) { toast.error((e1 || e2).message); return; }
    refresh();
  };

  const saveHeader = async () => {
    const { error } = await upsertSiteRules(siteId, {
      banner_image: draftBanner || null,
      intro_markdown: draftIntro || null,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Header saved.");
    setHeaderDirty(false);
    refresh();
  };

  const publicPath = `/site/${siteId}/rules`;

  return (
    <div data-testid="site-rules-edit-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <Link to={`/sites/${siteId}`} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 -ml-1 mb-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileText className="h-6 w-6" /> Site rules — {site?.name || "…"}
          </h1>
          <p className="text-sm text-slate-500">
            Public driver page: <code className="text-xs">{publicPath}</code>
          </p>
        </div>
        <Link to={publicPath} target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="rules-preview-link">
            <Eye className="h-4 w-4 mr-1.5" /> Preview public page
          </Button>
        </Link>
      </div>

      {/* Header (banner + intro) */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Page header</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="banner" className="text-xs text-slate-500">Banner image URL (optional)</Label>
            <Input
              id="banner"
              value={draftBanner}
              onChange={(e) => { setDraftBanner(e.target.value); setHeaderDirty(true); }}
              placeholder="https://..."
              data-testid="rules-banner-input"
            />
          </div>
          <div>
            <Label htmlFor="intro" className="text-xs text-slate-500">Intro text (Markdown)</Label>
            <Textarea
              id="intro"
              rows={3}
              value={draftIntro}
              onChange={(e) => { setDraftIntro(e.target.value); setHeaderDirty(true); }}
              placeholder="Welcome message shown above the sections…"
              data-testid="rules-intro-input"
            />
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={!headerDirty} onClick={saveHeader} data-testid="rules-save-header">
              <Save className="h-3.5 w-3.5 mr-1" /> Save header
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Sections ({sections.length})</h2>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" data-testid="rules-add-section-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add section
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SECTION_TYPES.map((p) => (
              <DropdownMenuItem
                key={p.type}
                onClick={() => addSection(p)}
                data-testid={`rules-add-${p.type}`}
              >
                {p.title}
                <span className="ml-auto text-[10px] text-slate-400 font-mono">{p.type}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-3">
        {sections.map((sec, idx) => (
          <Card key={sec.id} data-testid={`rules-section-${sec.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="secondary" className="text-[10px] font-mono shrink-0">{sec.section_type}</Badge>
                  {sec.is_internal && (
                    <Badge className="text-[10px] bg-slate-800 text-white shrink-0" data-testid={`rules-internal-badge-${sec.id}`}>
                      Internal
                    </Badge>
                  )}
                  {editingId === sec.id ? (
                    <Input
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      className="h-7 text-sm font-medium"
                      data-testid={`rules-title-input-${sec.id}`}
                    />
                  ) : (
                    <span className="font-medium truncate">{sec.title}</span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => move(idx, -1)} title="Move up" data-testid={`rules-up-${sec.id}`}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" disabled={idx === sections.length - 1} onClick={() => move(idx, 1)} title="Move down" data-testid={`rules-down-${sec.id}`}>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  {editingId === sec.id ? (
                    <>
                      <Button size="sm" onClick={() => saveEdit(sec)} data-testid={`rules-save-${sec.id}`}>
                        <Save className="h-3.5 w-3.5 mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => startEdit(sec)} data-testid={`rules-edit-${sec.id}`}>
                      Edit
                    </Button>
                  )}
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => toggleInternal(sec)}
                    title={sec.is_internal ? "Make visible on public page" : "Hide from public page (internal only)"}
                    data-testid={`rules-toggle-internal-${sec.id}`}
                  >
                    {sec.is_internal
                      ? <EyeOff className="h-4 w-4 text-slate-500" />
                      : <Eye    className="h-4 w-4 text-emerald-600" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => doDelete(sec)} data-testid={`rules-delete-${sec.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              </div>

              {editingId === sec.id ? (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Edit content</Label>
                  <SectionFormEditor
                    section={sec}
                    siteId={siteId}
                    onChange={setDraftData}
                    onChangeBody={setDraftBody}
                  />
                </div>
              ) : (
                <div className="border-t pt-3">
                  <SectionRenderer section={sec} />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {sections.length === 0 && (
          <Card><CardContent className="p-12 text-center text-sm text-slate-400" data-testid="rules-empty">
            No sections yet. Click <strong>Add section</strong> above to start.
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
